import React, { useEffect, useState } from "react";
import { Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Pagination } from "@/components/navigation/PaginationShadcn";
import { useProfile } from "@/_actions/userActions";
import { Link } from "react-router-dom";
import { DataTable } from "@/components/ui/data-table";
import { useGetResearches, usePayableResearches } from "@/_actions/researchActions";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import Dinero from "dinero.js";

const columns = [
  { accessorKey: "id", header: "ID de investigación" },
  {
    accessorFn: (row) => `${row.first_name} ${row.second_name || ""}`,
    header: "Nombre",
  },
  { accessorKey: "last_name", header: "Apellido" },
  { accessorKey: "email", header: "Correo" },
  { accessorKey: "phone", header: "Teléfono" },
  {
    accessorKey: "status",
    header: "Estatus",
    cell: ({ row }) => {
      const status = row.original.status;
      const statusLabels = {
        completed: "Completado",
        draft: "Iniciado",
        in_progress: "En progreso",
      };
      return <span className={`text-${status}-600`}>{statusLabels[status] || "Desconocido"}</span>;
    },
  },
  {
    accessorKey: "investigation_file",
    header: "Archivo",
    cell: ({ row }) => {
      const file = row.original.investigation_file;
      return file ? (
        <a href={file} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
          Ver PDF
        </a>
      ) : (
        <span className="text-gray-400">No disponible</span>
      );
    },
  },
  {
    accessorKey: "broker.user.first_name",
    header: "Nombre del Broker",
  },
];

export default function GeneralView() {
  const { data: payableResearches } = usePayableResearches();
  const { data: profile } = useProfile();
  const [filters, setFilters] = useState({
    status: "",
    first_name: "",
    email: "",
    referral_payment_status: "",
  });
  const [page, setPage] = useState(1);
  const {
    data,
    isLoading: isLoadingInvestigations,
    error: errorResearches,
  } = useGetResearches(filters, page);

  const handleFilterChange = (e) => {
    setPage(1);
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen">
      <div className="relative ">
        <div className="mx-auto ">
          <div className="w-full flex flex-col md:flex-row gap-3 ">
            <div className="relative min-h-42 md:min-h-auto mt-3 w-full max-w-xl bg-violet-cyt  rounded-xl shadow-md border border-gray-200">
              <div className="p-4">
                <div className=" justify-start text-Black text-3xl font-medium font-['Space_Grotesk']">
                  ¡Gana por cada cliente referido!
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  Comparte esta liga con tus clientes para que puedan iniciar una investigación.
                </p>
              </div>

              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-2 bg-black rounded-b-xl px-4 py-2">
                <span className="truncate  text-sm text-white" id="url">
                  {`${import.meta.env.VITE_PORTAL_URL}investigacion?broker=${
                    profile?.memberships[0]?.id
                  }`}
                </span>

                <Tooltip>
                  <TooltipTrigger>
                    <Copy
                      className="w-5 h-5 text-white cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        const content = document.getElementById("url").innerHTML;
                        toast.success("URL copiada al portapapeles");
                        navigator.clipboard.writeText(content).catch((err) => {
                          $log.error("Something went wrong", err);
                        });
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>Copiar URL</TooltipContent>
                </Tooltip>
              </div>
            </div>

            {/* <div className="mt-3 w-full max-w-xl bg-green-cyt  rounded-xl shadow-md border border-gray-200">
              <div className="p-4">
                <div className=" justify-start text-Black text-3xl font-medium font-['Space_Grotesk']">
                  Conoce más
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  Guía paso a paso para comenzar, comparte esta información con
                  tu inquilino para que inicie su proceso de investigación. con
                  resultados en menos de 15 minutos.
                </p>
              </div>
              <div className="text-right">
                <Button className="mr-3 my-2">Descargar</Button>
              </div>
            </div> */}
            <div className="mt-3 w-full max-w-xl bg-green-cyt  rounded-xl shadow-md border border-gray-200">
              <div className="p-4">
                <div className=" justify-start text-Black text-3xl font-medium font-['Space_Grotesk']">
                  {payableResearches
                    ? new Dinero({
                        amount: payableResearches?.subtotal,
                        currency: "MXN",
                      }).toFormat()
                    : "0.00"}
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  {payableResearches?.subtotal > 0
                    ? "Tienes comisiones por cobrar"
                    : "No tienes comisiones por cobrar. Empieza a usar las investigaciones para generar ingresos."}
                </p>
              </div>
              <div className="text-right">
                <Link to="/investigaciones/comisiones">
                  <Button className="mr-3 my-2 cursor-pointer">Cobrar comisiones</Button>
                </Link>
              </div>
            </div>
          </div>

          <div className="space-y-6 mt-6">
            <div className="flex flex-wrap gap-4 items-end bg-white p-5 rounded-2xl">
              <div className="flex items-center h-10">
                <h3 className="text-base sm:text-lg font-semibold whitespace-nowrap">
                  Filtrar por:
                </h3>
              </div>

              <div className="w-full sm:w-auto flex-1 min-w-[200px]">
                <Label htmlFor="first_name">Nombre</Label>
                <Input
                  name="first_name"
                  value={filters.first_name}
                  onChange={handleFilterChange}
                  placeholder="Ej. Juan"
                />
              </div>

              <div className="w-full sm:w-auto flex-1 min-w-[200px]">
                <Label htmlFor="email">Correo</Label>
                <Input
                  name="email"
                  value={filters.email}
                  onChange={handleFilterChange}
                  placeholder="ejemplo@email.com"
                />
              </div>

              <div className="w-full sm:w-auto flex-1 min-w-[200px]">
                <Label htmlFor="status" className="mb-2.5 block">
                  Estatus
                </Label>
                <Select
                  value={filters.status}
                  onValueChange={(value) =>
                    handleFilterChange({ target: { name: "status", value } })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Todos los estatus" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="completed">Completado</SelectItem>
                    <SelectItem value="draft">Iniciado</SelectItem>
                    <SelectItem value="in_progress">En progreso</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="w-full sm:w-auto flex-1 min-w-[200px]">
                {/* Pago de comisión */}
                <Label htmlFor="referral_payment_status" className="mb-2.5 block">
                  Pago de comisión
                </Label>
                <Select
                  value={filters.referral_payment_status}
                  onValueChange={(value) =>
                    handleFilterChange({
                      target: { name: "referral_payment_status", value },
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Todos los estatus" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="in_progress">En progreso</SelectItem>
                    <SelectItem value="paid">Pagado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {data?.results?.length > 0 ? (
              <div className="pb-14 bg-white p-5 rounded-2xl">
                <DataTable columns={columns} data={data.results} />

                <div className="mt-6 relative flex justify-center items-center px-2">
                  <span className="absolute left-0 text-sm text-muted-foreground">
                    Total: {data.total}
                  </span>

                  <Pagination
                    page={page}
                    totalPages={data.pages}
                    onPageChange={(newPage) => setPage(newPage)}
                  />
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500">No hay investigaciones que mostrar</p>
            )}

            {isLoadingInvestigations && (
              <p className="text-center text-gray-500">Cargando investigaciones...</p>
            )}

            {errorResearches && (
              <p className="text-center text-red-500">Error al cargar investigaciones</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
